import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Troque para true quando quiser forçar a pré-visualização do PDF em tela.
const isLocalhost =
	typeof window !== 'undefined' &&
	['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

// Ferramenta de ajuste visual disponível apenas no ambiente local.
export const SHOW_ESCALA_PDF_PREVIEW =
	process.env.NODE_ENV === 'development' && isLocalhost;

const PDF_PAGE_WIDTH = 794;
const PDF_PAGE_HEIGHT = 1123;
const PDF_PAGE_PADDING_X = 12;
const PDF_PAGE_PADDING_Y = 12;
const PDF_CAPTURE_SCALE = 1.85;
const PDF_IMAGE_QUALITY = 0.92;
const PDF_CAPTURE_CONCURRENCY = 2;
const PDF_MAX_CARD_SLOTS_PER_PAGE = 9;

const DIAS_ORDEM = [
	'Segunda-feira',
	'Terça-feira',
	'Quarta-feira',
	'Quinta-feira',
	'Sexta-feira',
	'Sábado',
	'Domingo',
];

const normalizarTexto = (texto = '') =>
	String(texto)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const normalizarDiaSemana = (dia = '') => {
	const base = normalizarTexto(dia);

	if (base.includes('segunda')) return 'Segunda-feira';
	if (base.includes('terca')) return 'Terça-feira';
	if (base.includes('quarta')) return 'Quarta-feira';
	if (base.includes('quinta')) return 'Quinta-feira';
	if (base.includes('sexta')) return 'Sexta-feira';
	if (base.includes('sabado')) return 'Sábado';
	if (base.includes('domingo')) return 'Domingo';

	return null;
};

const extrairRotulo = (valor) => {
	if (valor === null || valor === undefined) return '';
	if (typeof valor === 'string' || typeof valor === 'number') {
		return String(valor).trim();
	}

	if (typeof valor !== 'object') return '';

	const candidato =
		valor?.nome ||
		valor?.descricao ||
		valor?.label ||
		valor?.titulo ||
		valor?.codigo;

	if (candidato === valor) return '';
	return extrairRotulo(candidato);
};

const primeiroRotulo = (...valores) => {
	for (const valor of valores) {
		const rotulo = extrairRotulo(valor);
		if (rotulo) return rotulo;
	}
	return '';
};

const getEspecialidadeItem = (item, linha = null) =>
	primeiroRotulo(
		item?.especialidade,
		item?.especialidade_nome,
		item?.nome_especialidade,
		item?.profissional?.especialidade,
		item?.profissional?.especialidade_nome,
		item?.profissional?.nome_especialidade,
		item?.resumo?.especialidade,
		item?.edicao?.profissional_por_cpf?.especialidade_nome,
		item?.dados_edicao?.especialidade,
		item?.dados_edicao?.nome_especialidade,
		linha?.especialidade,
		linha?.vinculo_profissional?.especialidade,
	);

const getVinculoItem = (item, linha = null) =>
	primeiroRotulo(
		item?.vinculo,
		item?.nome_vinculo,
		item?.vinculo_nome,
		item?.descricao_vinculo,
		item?.profissional?.vinculo,
		item?.profissional?.vinculo_nome,
		item?.profissional?.nome_vinculo,
		item?.resumo?.vinculo,
		item?.edicao?.profissional_por_cpf?.vinculo_tipo_nome,
		item?.dados_edicao?.vinculo,
		item?.dados_edicao?.nome_vinculo,
		linha?.vinculo_profissional?.nome_vinculo,
		linha?.vinculo_profissional?.vinculo_tipo_nome,
		linha?.vinculo_profissional?.tipo_vinculo,
		linha?.vinculo_profissional?.vinculo,
		linha?.vinculo,
	);

const VINCULO_PALETA = [
	{ fundo: '#E7F2F6', texto: '#6E9DB5', borda: 'rgba(110,157,181,0.25)' },
	{ fundo: '#EAF5ED', texto: '#77A985', borda: 'rgba(119,169,133,0.25)' },
	{ fundo: '#ECEEFA', texto: '#8B8FC6', borda: 'rgba(139,143,198,0.25)' },
	{ fundo: '#F4E8F0', texto: '#B77FA8', borda: 'rgba(183,127,168,0.25)' },
	{ fundo: '#F4EDDF', texto: '#B99B6A', borda: 'rgba(185,155,106,0.25)' },
	{ fundo: '#F8EAE4', texto: '#D09882', borda: 'rgba(208,152,130,0.25)' },
	{ fundo: '#E8F4F4', texto: '#6FA6A9', borda: 'rgba(111,166,169,0.25)' },
	{ fundo: '#F0EAF6', texto: '#A58AC0', borda: 'rgba(165,138,192,0.25)' },
	{ fundo: '#E9F0F6', texto: '#7E9BB7', borda: 'rgba(126,155,183,0.25)' },
	{ fundo: '#EEF3E3', texto: '#9AAE72', borda: 'rgba(154,174,114,0.25)' },
];

const getVisualVinculo = (index = 0) => {
	const paletteIndex = Math.abs(Number(index) || 0) % VINCULO_PALETA.length;
	return VINCULO_PALETA[paletteIndex];
};

// Mantém a mesma sequência visual usada pelos blocos de especialidade da
// grade interativa. No PDF os blocos não são recolhíveis, mas a identidade
// cromática precisa continuar a mesma.
const ESPECIALIDADE_PALETA = [
	{ fundo: '#E8F3F8', texto: '#4F86A8' },
	{ fundo: '#E9F5F2', texto: '#5E9C8D' },
	{ fundo: '#F0EAF6', texto: '#9A7BB6' },
	{ fundo: '#F7E9EF', texto: '#C97D97' },
	{ fundo: '#F6F0D8', texto: '#B8A45C' },
	{ fundo: '#F6E8E1', texto: '#C58A72' },
	{ fundo: '#E7F2F6', texto: '#6E9DB5' },
	{ fundo: '#EAF5ED', texto: '#77A985' },
	{ fundo: '#ECEEFA', texto: '#8B8FC6' },
	{ fundo: '#F4E8F0', texto: '#B77FA8' },
	{ fundo: '#F4EDDF', texto: '#B99B6A' },
	{ fundo: '#F8EAE4', texto: '#D09882' },
	{ fundo: '#E8F4F4', texto: '#6FA6A9' },
	{ fundo: '#F0EAF6', texto: '#A58AC0' },
	{ fundo: '#E9F0F6', texto: '#7E9BB7' },
	{ fundo: '#EEF3E3', texto: '#9AAE72' },
];

const getVisualEspecialidade = (index = 0) =>
	ESPECIALIDADE_PALETA[
		Math.abs(Number(index) || 0) % ESPECIALIDADE_PALETA.length
	] || ESPECIALIDADE_PALETA[0];

const getVisualVinculoPorNome = (nome = '') => {
	let hash = 0;
	const texto = normalizarTexto(nome || 'Sem vínculo');

	for (let index = 0; index < texto.length; index += 1) {
		hash = (hash * 31 + texto.charCodeAt(index)) >>> 0;
	}

	return getVisualVinculo(hash % VINCULO_PALETA.length);
};

const STATUS_VISUAL = {
	VACANCIA: { fundo: '#BD504E', texto: '#FFFFFF' },
	FERIAS: { fundo: '#6ACEF8', texto: '#FFFFFF' },
	ATESTADO: { fundo: '#FB965C', texto: '#FFFFFF' },
	FOLGA: { fundo: '#8BBD8B', texto: '#FFFFFF' },
	LICENCA: { fundo: '#A476C7', texto: '#FFFFFF' },
	FALTA: { fundo: '#545454', texto: '#FFFFFF' },
	TROCA: { fundo: '#967771', texto: '#FFFFFF' },
	ATRIBUIDO: { fundo: 'transparent', texto: '#65686d' },
	NAO_ATRIBUIDO: { fundo: 'transparent', texto: '#65686d' },
};

const getPeriodoTurno = (linha = {}) => {
	const turno = normalizarTexto(
		`${linha.turnoKey || ''} ${linha.turnoNome || ''}`,
	);

	if (turno.includes('noite') || turno.includes('noturno')) return 'noite';
	if (turno.includes('tarde')) return 'tarde';
	return 'manha';
};

const getLinhaTurnoStyle = (linha = {}) => {
	const periodo = getPeriodoTurno(linha);

	if (periodo === 'noite') return { backgroundColor: '#DDE3E8' };
	if (periodo === 'tarde') return { backgroundColor: '#EEF1F4' };
	return { backgroundColor: '#FFFFFF' };
};

const getRegimeGrupoLinha = (linha = {}) => {
	const regimeLinha = normalizarTexto(
		linha.__regimeGrupo ||
			linha.regime ||
			linha?.contexto?.regime ||
			linha?.contexto_competencia?.regime ||
			linha?.faixa_horaria?.regime ||
			'',
	);

	if (regimeLinha.includes('plant')) return 'plantonista';
	if (regimeLinha.includes('rotina')) return 'rotina';

	const turno = normalizarTexto(
		`${linha?.turnoKey || linha?.faixa_horaria?.codigo || ''} ${
			linha?.turnoNome || linha?.faixa_horaria?.nome || ''
		}`,
	);

	if (turno.includes('diurno') || turno.includes('noturno')) {
		return 'plantonista';
	}

	return 'rotina';
};

const isRegimeTodos = (data = {}) => {
	const regime = normalizarTexto(
		data?.contexto?.regime ||
			data?.filtros?.regime ||
			data?.visualizacao?.regime ||
			data?.escala?.regime ||
			'',
	);

	return regime === 'todos' || regime === 'todo' || regime === 'all';
};

const getTurnoAgrupadoLinha = (
	linha = {},
	agruparRotinaEmPlantonista = false,
) => {
	const regimeGrupo = getRegimeGrupoLinha(linha);
	const turnoKeyOriginal =
		linha?.turnoKey || linha?.faixa_horaria?.codigo || 'turno';
	const turnoNomeOriginal =
		linha?.turnoNome || linha?.faixa_horaria?.nome || 'Turno';

	if (!agruparRotinaEmPlantonista) {
		return {
			turnoKey: turnoKeyOriginal,
			turnoNome: turnoNomeOriginal,
			regimeGrupo,
		};
	}

	const turnoNormalizado = normalizarTexto(
		`${turnoKeyOriginal} ${turnoNomeOriginal}`,
	);
	const isNoturno =
		turnoNormalizado.includes('noite') ||
		turnoNormalizado.includes('noturno');

	return {
		turnoKey: isNoturno ? 'NOTURNO' : 'DIURNO',
		turnoNome: isNoturno ? 'Noturno' : 'Diurno',
		regimeGrupo: 'plantonista',
	};
};

const parseISODateAsLocal = (valorData) => {
	if (!valorData) return null;

	const texto = String(valorData).trim();
	const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);

	if (!match) {
		const dataGenerica = new Date(texto);
		return Number.isNaN(dataGenerica.getTime()) ? null : dataGenerica;
	}

	const ano = Number(match[1]);
	const mes = Number(match[2]);
	const dia = Number(match[3]);
	const dataLocal = new Date(ano, mes - 1, dia, 0, 0, 0, 0);

	if (Number.isNaN(dataLocal.getTime())) return null;
	return dataLocal;
};

const getDiaSemanaNumero = (valorData) => {
	const data = parseISODateAsLocal(valorData);
	if (!data) return null;

	const diaJS = data.getDay();
	return diaJS === 0 ? 7 : diaJS;
};

const formatarDataPtBr = (valorData) => {
	if (!valorData) return '';
	const data = parseISODateAsLocal(valorData);
	if (!data) return String(valorData);
	if (Number.isNaN(data.getTime())) return String(valorData);
	return data.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
};

const extrairDataReal = (item, celula) =>
	item?.data ||
	item?.data_referencia ||
	item?.data_item ||
	item?.data_inicio ||
	celula?.data ||
	celula?.data_referencia ||
	celula?.data_item ||
	'';

// Omite sobrenomes finais quando o nome completo não cabe no card.
// Mantém pelo menos nome + primeiro sobrenome.
const smartTruncateName = (fullName = '') => {
	const name = String(fullName).trim();
	const words = name.split(/\s+/).filter(Boolean);
	if (words.length <= 2) return name;

	const MAX_CHARS = 20;
	if (name.length <= MAX_CHARS) return name;

	for (let count = words.length - 1; count >= 2; count -= 1) {
		const candidate = words.slice(0, count).join(' ');
		if (candidate.length <= MAX_CHARS) {
			return candidate;
		}
	}

	return words.slice(0, 2).join(' ');
};

const getHojeBrasiliaISO = () => {
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'America/Sao_Paulo',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});

	const parts = formatter.formatToParts(new Date());
	const year = parts.find((part) => part.type === 'year')?.value;
	const month = parts.find((part) => part.type === 'month')?.value;
	const day = parts.find((part) => part.type === 'day')?.value;

	if (!year || !month || !day) return '';
	return `${year}-${month}-${day}`;
};

const buildSemanasDoMes = (colunas = []) => {
	if (!Array.isArray(colunas) || colunas.length === 0) return [];

	const colunasOrdenadas = [...colunas].sort((a, b) => {
		const dataA = String(a?.data || '');
		const dataB = String(b?.data || '');
		return dataA.localeCompare(dataB);
	});

	const semanas = [];
	let semanaAtual = [];

	colunasOrdenadas.forEach((coluna) => {
		semanaAtual.push(coluna);

		const diaSemana =
			getDiaSemanaNumero(coluna?.data) ?? Number(coluna?.dia_semana);

		if (diaSemana === 7) {
			semanas.push(semanaAtual);
			semanaAtual = [];
		}
	});

	if (semanaAtual.length) semanas.push(semanaAtual);

	return semanas;
};

const construirColunasSemana = (visualizacao = {}, semanaSelecionadaIndex) => {
	const colunas = visualizacao?.colunas || [];
	const semanasDoMes = buildSemanasDoMes(colunas);
	if (!semanasDoMes.length) {
		return DIAS_ORDEM.map((dia) => ({
			nome_dia_semana: dia,
			data: '',
			dia_mes: '',
		}));
	}

	let indiceSemana = semanaSelecionadaIndex;
	if (
		typeof indiceSemana !== 'number' ||
		indiceSemana < 0 ||
		indiceSemana >= semanasDoMes.length
	) {
		const hojeISO = getHojeBrasiliaISO();
		const indiceSemanaAtual = semanasDoMes.findIndex((semana) =>
			semana.some((coluna) => coluna?.data === hojeISO),
		);
		indiceSemana = indiceSemanaAtual >= 0 ? indiceSemanaAtual : 0;
	}

	const semanaSelecionada = semanasDoMes[indiceSemana] || [];
	const porDia = new Map();

	semanaSelecionada.forEach((coluna) => {
		const diaNormalizado = normalizarDiaSemana(coluna?.nome_dia_semana);
		if (!diaNormalizado) return;
		porDia.set(diaNormalizado, coluna);
	});

	return DIAS_ORDEM.map(
		(dia) =>
			porDia.get(dia) || {
				nome_dia_semana: dia,
				data: '',
				dia_mes: '',
			},
	);
};

const getHoraItemRenderizacao = (item = {}, celula = {}, linha = {}) => {
	const horarioItem = getHorarioItem(item) || {};
	return {
		hora_inicio:
			horarioItem?.hora_inicio ||
			item?.hora_inicio ||
			celula?.hora_inicio ||
			linha?.regime_escala?.hora_inicio ||
			linha?.hora_inicio ||
			'',
		hora_fim:
			horarioItem?.hora_fim ||
			item?.hora_fim ||
			celula?.hora_fim ||
			linha?.regime_escala?.hora_fim ||
			linha?.hora_fim ||
			'',
	};
};

const horaParaMinutos = (hora = '') => {
	const [horas, minutos] = String(hora || '')
		.slice(0, 5)
		.split(':')
		.map((parte) => Number(parte));
	if (!Number.isFinite(horas)) return null;
	return horas * 60 + (Number.isFinite(minutos) ? minutos : 0);
};

const somarDiasDataISO = (dataISO = '', dias = 0) => {
	if (!dataISO) return '';
	const dataBase = new Date(`${dataISO}T00:00:00`);
	if (Number.isNaN(dataBase.getTime())) return '';
	dataBase.setDate(dataBase.getDate() + dias);
	return dataBase.toISOString().slice(0, 10);
};

const getTurnoPlantonistaKeyLinha = (linha = {}) => {
	const turno = normalizarTexto(
		`${linha?.turnoKey || linha?.faixa_horaria?.codigo || ''} ${
			linha?.turnoNome || linha?.faixa_horaria?.nome || ''
		}`,
	);

	if (turno.includes('noturno')) return 'NOTURNO';
	if (turno.includes('diurno')) return 'DIURNO';
	return '';
};

const getHoraInicioLinhaTurno = (linha = {}) =>
	String(
		linha?.regime_escala?.hora_inicio ||
			linha?.faixa_horaria?.hora_inicio ||
			linha?.hora_inicio ||
			'',
	).slice(0, 5);

const isPlantao24hRenderizacao = (item = {}, celula = {}, linha = {}) => {
	if (getRegimeGrupoLinha(linha) !== 'plantonista') return false;
	const { hora_inicio, hora_fim } = getHoraItemRenderizacao(
		item,
		celula,
		linha,
	);
	const horaInicio = String(hora_inicio || '').slice(0, 5);
	const horaFim = String(hora_fim || '').slice(0, 5);

	return Boolean(horaInicio && horaFim && horaInicio === horaFim);
};

const ajustarItemPlantao24hParaTurno = ({
	item,
	celula,
	linha,
	turnoKey,
	limiteDiurno,
	limiteNoturno,
	horarioRenderizacaoOverride = null,
	isDuplicado = false,
}) => {
	const { hora_inicio, hora_fim } = getHoraItemRenderizacao(
		item,
		celula,
		linha,
	);
	const horaInicioOriginal = String(hora_inicio || '').slice(0, 5);
	const horaFimOriginal = String(hora_fim || '').slice(0, 5);

	if (!horaInicioOriginal || !horaFimOriginal) return item;

	const inicioMinutos = horaParaMinutos(horaInicioOriginal);
	const limiteNoturnoMinutos = horaParaMinutos(limiteNoturno);
	const iniciaNoPeriodoNoturno =
		inicioMinutos !== null &&
		limiteNoturnoMinutos !== null &&
		inicioMinutos >= limiteNoturnoMinutos;

	let horarioRenderizacao = horarioRenderizacaoOverride;

	if (!horarioRenderizacao) {
		horarioRenderizacao =
			turnoKey === 'NOTURNO'
				? iniciaNoPeriodoNoturno
					? {
							hora_inicio: horaInicioOriginal,
							hora_fim: limiteDiurno,
						}
					: { hora_inicio: limiteNoturno, hora_fim: horaFimOriginal }
				: iniciaNoPeriodoNoturno
					? { hora_inicio: limiteDiurno, hora_fim: horaFimOriginal }
					: {
							hora_inicio: horaInicioOriginal,
							hora_fim: limiteNoturno,
						};
	}

	return {
		...item,
		...(isDuplicado ? { __plantao24hDuplicado: true } : {}),
		__plantao24hRenderizacao: true,
		__horarioPlantao24hRenderizacao: horarioRenderizacao,
	};
};

const duplicarPlantoes24hEntreTurnos = (linhasBase = [], itensPorId = {}) => {
	const linhasClonadas = linhasBase.map((linha) => ({
		...linha,
		celulas: Array.isArray(linha?.celulas)
			? linha.celulas.map((celula) => ({
					...celula,
					cards: Array.isArray(celula?.cards)
						? [...celula.cards]
						: celula?.cards,
					itens_ids: Array.isArray(celula?.itens_ids)
						? [...celula.itens_ids]
						: celula?.itens_ids,
				}))
			: [],
	}));
	const linhasPorTurno = new Map();
	const duplicacoes = [];

	linhasClonadas.forEach((linha) => {
		if (getRegimeGrupoLinha(linha) !== 'plantonista') return;
		const turnoKey = getTurnoPlantonistaKeyLinha(linha);
		if (!turnoKey) return;
		linhasPorTurno.set(turnoKey, linha);
	});

	const limiteDiurno =
		getHoraInicioLinhaTurno(linhasPorTurno.get('DIURNO')) || '07:00';
	const limiteNoturno =
		getHoraInicioLinhaTurno(linhasPorTurno.get('NOTURNO')) || '19:00';
	const criarHorariosSegmentos24h = ({ horaInicioOriginal }) => {
		const inicioMinutos = horaParaMinutos(horaInicioOriginal);
		const limiteNoturnoMinutos = horaParaMinutos(limiteNoturno);
		const iniciaNoPeriodoNoturno =
			inicioMinutos !== null &&
			limiteNoturnoMinutos !== null &&
			inicioMinutos >= limiteNoturnoMinutos;

		if (iniciaNoPeriodoNoturno) {
			return {
				origem: { hora_inicio: horaInicioOriginal, hora_fim: '00:00' },
				proximoDiaDiurno: {
					hora_inicio: '00:00',
					hora_fim: limiteDiurno,
				},
				proximoDiaMesmoTurno: {
					hora_inicio: limiteDiurno,
					hora_fim: horaInicioOriginal,
				},
			};
		}

		return {
			origem: {
				hora_inicio: horaInicioOriginal,
				hora_fim: limiteNoturno,
			},
			mesmoDiaOutroTurno: {
				hora_inicio: limiteNoturno,
				hora_fim: '00:00',
			},
			proximoDiaDiurno: {
				hora_inicio: '00:00',
				hora_fim: horaInicioOriginal,
			},
		};
	};
	const inserirSegmentoPlantao24h = ({
		item,
		id,
		celula,
		dataDestino,
		turnoDestino,
		horarioRenderizacao,
	}) => {
		if (!dataDestino || !turnoDestino || !horarioRenderizacao) return;

		const linhaDestino = linhasPorTurno.get(turnoDestino);
		if (!linhaDestino) return;

		let celulaDestino = (linhaDestino.celulas || []).find(
			(celulaAtual) =>
				String(celulaAtual?.data || '') === String(dataDestino),
		);

		if (!celulaDestino) {
			celulaDestino = {
				...celula,
				data: dataDestino,
				cards: [],
				itens_ids: [],
			};
			linhaDestino.celulas = [
				...(linhaDestino.celulas || []),
				celulaDestino,
			];
		}

		const destinoCards = Array.isArray(celulaDestino.cards)
			? celulaDestino.cards
			: [];
		const segmentKey = `${id || 'item'}-${dataDestino}-${turnoDestino}-${horarioRenderizacao.hora_inicio}-${horarioRenderizacao.hora_fim}`;
		const jaExiste = destinoCards.some(
			(card) => card?.__plantao24hSegmentKey === segmentKey,
		);

		if (!jaExiste) {
			destinoCards.push(
				ajustarItemPlantao24hParaTurno({
					item,
					celula,
					linha: linhaDestino,
					turnoKey: turnoDestino,
					limiteDiurno,
					limiteNoturno,
					horarioRenderizacaoOverride: horarioRenderizacao,
					isDuplicado: true,
				}),
			);
			destinoCards[destinoCards.length - 1].__plantao24hSegmentKey =
				segmentKey;
		}

		celulaDestino.cards = destinoCards;
	};

	linhasClonadas.forEach((linha) => {
		if (getRegimeGrupoLinha(linha) !== 'plantonista') return;

		const turnoOrigem = getTurnoPlantonistaKeyLinha(linha);
		const turnoDestino =
			turnoOrigem === 'DIURNO'
				? 'NOTURNO'
				: turnoOrigem === 'NOTURNO'
					? 'DIURNO'
					: '';

		if (!turnoDestino) return;

		(linha?.celulas || []).forEach((celula) => {
			const cardsOriginais =
				Array.isArray(celula?.cards) && celula.cards.length > 0
					? celula.cards
					: (celula?.itens_ids || [])
							.map((id) => itensPorId[id])
							.filter(Boolean);
			const cards = cardsOriginais.map((item) => {
				if (!isPlantao24hRenderizacao(item, celula, linha)) return item;

				const { hora_inicio } = getHoraItemRenderizacao(
					item,
					celula,
					linha,
				);
				const segmentos = criarHorariosSegmentos24h({
					horaInicioOriginal: String(hora_inicio || '').slice(0, 5),
				});

				return ajustarItemPlantao24hParaTurno({
					item,
					celula,
					linha,
					turnoKey: turnoOrigem,
					limiteDiurno,
					limiteNoturno,
					horarioRenderizacaoOverride: segmentos.origem,
				});
			});

			if (cards.length > 0) {
				celula.cards = cards;
			}

			cards.forEach((item) => {
				if (!isPlantao24hRenderizacao(item, celula, linha)) return;
				duplicacoes.push({
					item,
					id: item?.id,
					celula,
					linhaOrigem: linha,
					turnoOrigem,
					turnoDestino,
				});
			});
		});
	});

	duplicacoes.forEach(
		({ item, id, celula, linhaOrigem, turnoOrigem, turnoDestino }) => {
			const dataCelula = String(celula?.data || '');
			const { hora_inicio } = getHoraItemRenderizacao(
				item,
				celula,
				linhaOrigem,
			);
			const segmentos = criarHorariosSegmentos24h({
				horaInicioOriginal: String(hora_inicio || '').slice(0, 5),
			});
			const dataProximoDia = somarDiasDataISO(dataCelula, 1);

			if (turnoOrigem === 'DIURNO') {
				inserirSegmentoPlantao24h({
					item,
					id,
					celula,
					dataDestino: dataCelula,
					turnoDestino,
					horarioRenderizacao: segmentos.mesmoDiaOutroTurno,
				});
				inserirSegmentoPlantao24h({
					item,
					id,
					celula,
					dataDestino: dataProximoDia,
					turnoDestino: 'DIURNO',
					horarioRenderizacao: segmentos.proximoDiaDiurno,
				});
				return;
			}

			inserirSegmentoPlantao24h({
				item,
				id,
				celula,
				dataDestino: dataProximoDia,
				turnoDestino: 'DIURNO',
				horarioRenderizacao: segmentos.proximoDiaDiurno,
			});
			inserirSegmentoPlantao24h({
				item,
				id,
				celula,
				dataDestino: dataProximoDia,
				turnoDestino,
				horarioRenderizacao: segmentos.proximoDiaMesmoTurno,
			});
		},
	);

	return linhasClonadas;
};

const construirLinhasSetor = (setor, itensPorId = {}, data = {}) => {
	const linhasBase = Array.isArray(setor?.linhas) ? setor.linhas : [];
	const linhasOriginais = duplicarPlantoes24hEntreTurnos(
		linhasBase,
		itensPorId,
	);
	const nomeSetor = setor?.nome_exibicao || setor?.nome || 'Setor';
	const agruparRotinaEmPlantonista = isRegimeTodos(data);

	const turnos = new Map();
	const visualPorVinculo = new Map();
	let proximoIndiceVisual = 0;

	const getTurnoMapaKey = (linha = {}) =>
		`${getTurnoAgrupadoLinha(linha, agruparRotinaEmPlantonista).regimeGrupo}:${
			getTurnoAgrupadoLinha(linha, agruparRotinaEmPlantonista).turnoKey
		}`;

	const getVisualPorNome = (nome = 'Sem vínculo') => {
		const key = normalizarTexto(nome || 'Sem vínculo');
		if (!visualPorVinculo.has(key)) {
			visualPorVinculo.set(key, {
				...getVisualVinculo(proximoIndiceVisual),
				nome: nome || 'Sem vínculo',
				key,
			});
			proximoIndiceVisual += 1;
		}
		return visualPorVinculo.get(key);
	};

	linhasOriginais.forEach((linha, linhaIndex) => {
		const turnoAgrupado = getTurnoAgrupadoLinha(
			linha,
			agruparRotinaEmPlantonista,
		);
		const { turnoKey, turnoNome, regimeGrupo } = turnoAgrupado;
		const turnoMapaKey = getTurnoMapaKey(linha);
		const turnoAtual = turnos.get(turnoMapaKey) || {
			turnoKey,
			turnoNome,
			ordem: Number(linha?.ordem ?? linhaIndex),
			regimeGrupo,
			linhas: [],
			vinculos: new Map(),
		};

		turnoAtual.linhas.push(linha);

		const vinculoLinha = getVinculoItem({}, linha);
		if (vinculoLinha) {
			turnoAtual.vinculos.set(
				normalizarTexto(vinculoLinha),
				vinculoLinha,
			);
		}

		(linha?.celulas || []).forEach((celula) => {
			const cards =
				Array.isArray(celula?.cards) && celula.cards.length > 0
					? celula.cards
					: (celula?.itens_ids || [])
							.map((id) => itensPorId[id])
							.filter(Boolean);

			cards.forEach((item) => {
				const nomeVinculo =
					getVinculoItem(item, linha) || 'Sem vínculo';
				turnoAtual.vinculos.set(
					normalizarTexto(nomeVinculo),
					nomeVinculo,
				);
			});
		});

		turnos.set(turnoMapaKey, turnoAtual);
	});

	let base = Array.from(turnos.values()).flatMap((turno) => {
		const vinculosTurno =
			turno.vinculos.size > 0
				? Array.from(turno.vinculos.entries())
				: [[`sem-vinculo-${turno.turnoKey}`, 'Sem vínculo']];

		return vinculosTurno
			.sort(([, nomeA], [, nomeB]) => nomeA.localeCompare(nomeB, 'pt-BR'))
			.map(([vinculoKey, vinculoNome], vinculoIndex) => {
				const celulasPorData = new Map();

				turno.linhas.forEach((linhaOrigem) => {
					(linhaOrigem?.celulas || []).forEach((celula) => {
						const cards =
							Array.isArray(celula?.cards) &&
							celula.cards.length > 0
								? celula.cards
								: (celula?.itens_ids || [])
										.map((id) => itensPorId[id])
										.filter(Boolean);

						const cardsVinculo = cards
							.filter((item) => {
								const nomeItem =
									getVinculoItem(item, linhaOrigem) ||
									'Sem vínculo';
								return normalizarTexto(nomeItem) === vinculoKey;
							})
							.map((item) => ({
								...item,
								__linhaOrigem: linhaOrigem,
							}));

						if (cardsVinculo.length === 0) return;

						const dataCelula = String(celula?.data || '');
						const celulaAgrupada = celulasPorData.get(
							dataCelula,
						) || {
							...celula,
							cards: [],
							itens_ids: [],
						};

						celulaAgrupada.cards.push(...cardsVinculo);
						celulaAgrupada.itens_ids.push(
							...cardsVinculo
								.map((item) => item?.id)
								.filter(
									(id) => id !== undefined && id !== null,
								),
						);
						celulasPorData.set(dataCelula, celulaAgrupada);
					});
				});

				const visualVinculo = getVisualPorNome(
					vinculoNome || 'Sem vínculo',
				);

				return {
					setorNome: nomeSetor,
					turnoNome: turno.turnoNome,
					turnoKey: turno.turnoKey,
					ordem: turno.ordem,
					linhaKey: `${turno.regimeGrupo}-${turno.turnoKey}-vinculo-${vinculoKey}-${vinculoIndex}`,
					celulas: Array.from(celulasPorData.values()),
					vinculoNome: vinculoNome || 'Sem vínculo',
					visualVinculo,
					__regimeGrupo: turno.regimeGrupo,
				};
			});
	});

	if (base.length === 0) {
		base = [
			{
				setorNome: nomeSetor,
				turnoNome: 'Turno',
				turnoKey: 'turno',
				ordem: 1,
				linhaKey: 'fallback-1',
				celulas: [],
				vinculoNome: 'Sem vínculo',
				visualVinculo: getVisualPorNome('Sem vínculo'),
				__regimeGrupo: 'rotina',
			},
		];
	}

	const TURNO_ORDEM = ['DIURNO', 'NOTURNO', 'MANHA', 'TARDE', 'NOITE'];
	const REGIME_ORDEM = { plantonista: 0, rotina: 1 };
	const getTurnoOrdem = (key = '', nome = '', regimeGrupo = '') => {
		const candidatos = [key, nome];
		let melhorIndice = TURNO_ORDEM.length;

		candidatos.forEach((valor) => {
			const normalized = String(valor || '')
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toUpperCase()
				.trim();
			const idx = TURNO_ORDEM.indexOf(normalized);
			if (idx !== -1 && idx < melhorIndice) {
				melhorIndice = idx;
			}
		});

		const regimeOffset =
			REGIME_ORDEM[regimeGrupo] !== undefined
				? REGIME_ORDEM[regimeGrupo] * 100
				: 200;

		return regimeOffset + melhorIndice;
	};

	const linhas = base.sort((a, b) => {
		const ordemA = getTurnoOrdem(a.turnoKey, a.turnoNome, a.__regimeGrupo);
		const ordemB = getTurnoOrdem(b.turnoKey, b.turnoNome, b.__regimeGrupo);
		if (ordemA !== ordemB) return ordemA - ordemB;
		if (a.turnoKey === b.turnoKey) return a.ordem - b.ordem;
		return String(a.turnoKey).localeCompare(String(b.turnoKey));
	});

	return {
		linhas,
		legendaVinculos: Array.from(visualPorVinculo.values()).sort((a, b) =>
			a.nome.localeCompare(b.nome, 'pt-BR'),
		),
	};
};

// Espelha a composição atual do ApiScaleGrid quando a visualização está
// agrupada por especialidade. Cada especialidade vira uma linha do turno e o
// cabeçalho colorido é renderizado pela tabela do PDF sempre expandido.
const construirLinhasSetorAtualizada = ({
	setor,
	itensPorId = {},
	data = {},
	agruparPorEspecialidade = false,
	regime = '',
}) => {
	const linhasBase = Array.isArray(setor?.linhas) ? setor.linhas : [];
	const nomeSetor = setor?.nome_exibicao || setor?.nome || 'Setor';
	const regimeNormalizado = normalizarTexto(regime);
	const agruparRotinaEmPlantonista =
		regimeNormalizado === 'todos' ||
		regimeNormalizado === 'todo' ||
		regimeNormalizado === 'all' ||
		isRegimeTodos(data);
	const linhasOriginais = duplicarPlantoes24hEntreTurnos(
		linhasBase,
		itensPorId,
	);
	const deveFiltrarRegime =
		regimeNormalizado === 'plantonista' || regimeNormalizado === 'rotina';
	const linhasVisiveis = deveFiltrarRegime
		? linhasOriginais.filter(
				(linha) => getRegimeGrupoLinha(linha) === regimeNormalizado,
			)
		: linhasOriginais;
	const getTurnoKey = (linha = {}) => {
		const turno = getTurnoAgrupadoLinha(linha, agruparRotinaEmPlantonista);
		return `${turno.regimeGrupo}:${turno.turnoKey}`;
	};
	const ordenarLinhas = (linhas = []) => {
		const ordemTurno = ['DIURNO', 'NOTURNO', 'MANHA', 'TARDE', 'NOITE'];
		const ordemRegime = { plantonista: 0, rotina: 1 };
		return [...linhas].sort((a, b) => {
			const regimeA = ordemRegime[a.__regimeGrupo] ?? 2;
			const regimeB = ordemRegime[b.__regimeGrupo] ?? 2;
			if (regimeA !== regimeB) return regimeA - regimeB;
			const turnoA = ordemTurno.indexOf(
				String(a.turnoKey || '').toUpperCase(),
			);
			const turnoB = ordemTurno.indexOf(
				String(b.turnoKey || '').toUpperCase(),
			);
			if (turnoA !== turnoB)
				return (turnoA < 0 ? 99 : turnoA) - (turnoB < 0 ? 99 : turnoB);
			return String(a.agrupamentoNome || '').localeCompare(
				String(b.agrupamentoNome || ''),
				'pt-BR',
			);
		});
	};
	const criarLinha = (linha, linhaIndex) => {
		const turno = getTurnoAgrupadoLinha(linha, agruparRotinaEmPlantonista);
		return {
			setorNome: nomeSetor,
			turnoNome: turno.turnoNome,
			turnoKey: turno.turnoKey,
			ordem: Number(linha?.ordem ?? linhaIndex),
			linhaKey:
				linha?.linha_id ||
				`${setor?.id || 'setor'}-${linhaIndex}-${turno.turnoKey}`,
			celulas: linha?.celulas || [],
			__regimeGrupo: turno.regimeGrupo,
		};
	};

	if (!agruparPorEspecialidade) {
		return {
			linhas: ordenarLinhas(linhasVisiveis.map(criarLinha)),
			legendaAgrupamentos: [],
		};
	}

	const turnos = new Map();
	linhasVisiveis.forEach((linha, linhaIndex) => {
		const turno = getTurnoAgrupadoLinha(linha, agruparRotinaEmPlantonista);
		const turnoKey = getTurnoKey(linha);
		const turnoAtual = turnos.get(turnoKey) || {
			...turno,
			ordem: Number(linha?.ordem ?? linhaIndex),
			linhas: [],
			agrupamentos: new Map(),
		};
		turnoAtual.linhas.push(linha);

		const especialidadeLinha = getEspecialidadeItem({}, linha);
		if (especialidadeLinha) {
			turnoAtual.agrupamentos.set(
				normalizarTexto(especialidadeLinha),
				especialidadeLinha,
			);
		}

		(linha?.celulas || []).forEach((celula) => {
			obterItensDaCelula(celula, itensPorId).forEach((item) => {
				const especialidade = getEspecialidadeItem(item, linha);
				if (!especialidade) return;
				turnoAtual.agrupamentos.set(
					normalizarTexto(especialidade),
					especialidade,
				);
			});
		});
		turnos.set(turnoKey, turnoAtual);
	});

	const linhasAgrupadas = Array.from(turnos.values()).flatMap((turno) =>
		Array.from(turno.agrupamentos.entries())
			.sort(([, nomeA], [, nomeB]) => nomeA.localeCompare(nomeB, 'pt-BR'))
			.map(([especialidadeKey, especialidadeNome], grupoIndex) => {
				const celulasPorData = new Map();
				turno.linhas.forEach((linhaOrigem) => {
					(linhaOrigem?.celulas || []).forEach((celula) => {
						const cards = obterItensDaCelula(celula, itensPorId)
							.filter(
								(item) =>
									normalizarTexto(
										getEspecialidadeItem(item, linhaOrigem),
									) === especialidadeKey,
							)
							.map((item) => ({
								...item,
								__linhaOrigem: linhaOrigem,
							}));
						if (cards.length === 0) return;

						const dataCelula = String(celula?.data || '');
						const agrupada = celulasPorData.get(dataCelula) || {
							...celula,
							cards: [],
							itens_ids: [],
						};
						agrupada.cards.push(...cards);
						agrupada.itens_ids.push(
							...cards
								.map((item) => item?.id)
								.filter(
									(id) => id !== undefined && id !== null,
								),
						);
						celulasPorData.set(dataCelula, agrupada);
					});
				});

				return {
					setorNome: nomeSetor,
					turnoNome: turno.turnoNome,
					turnoKey: turno.turnoKey,
					ordem: turno.ordem,
					linhaKey: `${turno.regimeGrupo}-${turno.turnoKey}-especialidade-${especialidadeKey}-${grupoIndex}`,
					celulas: Array.from(celulasPorData.values()),
					agrupamentoNome: especialidadeNome,
					__temAgrupamentoReal: true,
					__regimeGrupo: turno.regimeGrupo,
				};
			}),
	);

	const linhas = ordenarLinhas(
		linhasAgrupadas.length > 0
			? linhasAgrupadas
			: linhasVisiveis.map(criarLinha),
	);
	const visualPorAgrupamento = new Map();
	let proximoIndiceVisual = 0;
	const linhasComVisual = linhas.map((linha) => {
		if (!linha.__temAgrupamentoReal) return linha;
		const key = normalizarTexto(linha.agrupamentoNome);
		if (!visualPorAgrupamento.has(key)) {
			visualPorAgrupamento.set(key, {
				...getVisualEspecialidade(proximoIndiceVisual),
				key,
				nome: linha.agrupamentoNome,
			});
			proximoIndiceVisual += 1;
		}
		const visual = visualPorAgrupamento.get(key);
		return {
			...linha,
			especialidadeFundo: visual.fundo,
			especialidadeTextoCor: visual.texto,
		};
	});

	return {
		linhas: linhasComVisual,
		legendaAgrupamentos: Array.from(visualPorAgrupamento.values()).sort(
			(a, b) => a.nome.localeCompare(b.nome, 'pt-BR'),
		),
	};
};

const construirTurnoSpan = (linhas = []) => {
	const spans = {};
	let index = 0;

	while (index < linhas.length) {
		const turnoAtual = linhas[index].turnoKey;
		let fim = index;

		while (fim < linhas.length && linhas[fim].turnoKey === turnoAtual) {
			fim += 1;
		}

		const rowSpan = fim - index;
		spans[linhas[index].linhaKey] = { show: true, rowSpan };

		for (let i = index + 1; i < fim; i += 1) {
			spans[linhas[i].linhaKey] = { show: false, rowSpan: 0 };
		}

		index = fim;
	}

	return spans;
};

const obterItensDaCelula = (celula, itensPorId) => {
	if (!celula) return [];
	if (Array.isArray(celula.cards) && celula.cards.length > 0) {
		return celula.cards;
	}

	return (celula.itens_ids || []).map((id) => itensPorId[id]).filter(Boolean);
};

const setorTemRegistrosNoMes = (setor, itensPorId) => {
	const linhas = setor?.linhas || [];

	for (let i = 0; i < linhas.length; i += 1) {
		const celulas = linhas[i]?.celulas || [];

		for (let j = 0; j < celulas.length; j += 1) {
			const itens = obterItensDaCelula(celulas[j], itensPorId);
			if (itens.length > 0) return true;
		}
	}

	return false;
};

const getNomeUnidade = (data, setor) =>
	setor?.unidade?.nome ||
	setor?.nome_unidade ||
	data?.contexto?.estabelecimento?.nome ||
	data?.contexto?.estabelecimento?.razao_social ||
	data?.contexto?.estabelecimento?.nome_fantasia ||
	data?.escala?.nome_estabelecimento ||
	data?.escala?.estabelecimento ||
	data?.escala?.unidade ||
	data?.contexto?.unidade?.nome ||
	'--';

const formatarHora = (hora) => (hora ? hora.slice(0, 5) : '--:--');

const getHorarioItem = (item = {}) =>
	item?.horario ||
	item?.resumo?.horario ||
	item?.edicao?.horario ||
	item?.dados_edicao?.horario ||
	null;

const getTipoLabelByItem = (item, getTipoKeyByItem) => {
	const tipo = getTipoKeyByItem(item);

	if (tipo === 'vacancia') return 'VACÂNCIA';
	if (tipo === 'troca') return 'TROCA';
	if (tipo === 'atestado') return 'ATESTADO';
	if (tipo === 'folga' || tipo === 'filga') return 'FOLGA';
	if (tipo === 'licenca' || tipo === 'licença') return 'LICENÇA';
	if (tipo === 'falta') return 'FALTA';
	if (tipo === 'ferias') return 'FÉRIAS';
	if (tipo === 'alocacao') return 'ALOCAÇÃO';

	return (item?.tipo_item || 'TIPO').toUpperCase();
};

const getStyleByItem = (item, getTipoKeyByItem) => {
	const tipo = getTipoKeyByItem(item);
	const status =
		tipo === 'vacancia'
			? 'VACANCIA'
			: tipo === 'ferias'
				? 'FERIAS'
				: tipo === 'atestado'
					? 'ATESTADO'
					: tipo === 'folga' || tipo === 'filga'
						? 'FOLGA'
						: tipo === 'licenca' || tipo === 'licença'
							? 'LICENCA'
							: tipo === 'falta'
								? 'FALTA'
								: tipo === 'troca'
									? 'TROCA'
									: tipo === 'alocacao'
										? 'ATRIBUIDO'
										: 'NAO_ATRIBUIDO';
	const visual = STATUS_VISUAL[status] || STATUS_VISUAL.NAO_ATRIBUIDO;

	return {
		badgeStyle: {
			backgroundColor: visual.fundo,
			borderColor:
				visual.fundo === 'transparent' ? 'transparent' : visual.fundo,
			color: visual.texto,
		},
		typeTextStyle: {
			color: visual.fundo === 'transparent' ? '#65686d' : visual.fundo,
		},
	};
};

const deveMostrarObservacao = (item, getTipoKeyByItem) => {
	const tipo = getTipoKeyByItem(item);
	return (
		tipo === 'atestado' ||
		tipo === 'folga' ||
		tipo === 'filga' ||
		tipo === 'falta' ||
		tipo === 'licenca' ||
		tipo === 'licença'
	);
};

const deveMostrarEspecialidadeEVinculo = (item, getTipoKeyByItem) => {
	const tipo = getTipoKeyByItem(item);

	return [
		'alocacao',
		'troca',
		'ferias',
		'vacancia',
		'atestado',
		'folga',
		'filga',
		'falta',
		'licenca',
		'licença',
	].includes(tipo);
};

const getConteudoSecundarioItem = (item, tipoItem, tipoLabel) => {
	if (tipoItem === 'vacancia') return '';

	if (
		tipoItem === 'ferias' ||
		tipoItem === 'atestado' ||
		tipoItem === 'folga' ||
		tipoItem === 'filga' ||
		tipoItem === 'falta' ||
		tipoItem === 'licenca' ||
		tipoItem === 'licença'
	) {
		return tipoLabel;
	}

	const horario =
		item?.horario || item?.resumo?.horario || item?.edicao?.horario || null;
	const horarioPlantao24h = item?.__horarioPlantao24hRenderizacao || null;
	if (horarioPlantao24h?.hora_inicio || horarioPlantao24h?.hora_fim) {
		return `${formatarHora(horarioPlantao24h?.hora_inicio)} às ${formatarHora(horarioPlantao24h?.hora_fim)}`;
	}

	const faixaHorariaTexto =
		horario?.faixa_horaria_texto ||
		horario?.nome_faixa_horaria ||
		item?.resumo?.faixa_horaria_texto ||
		item?.resumo?.nome_faixa_horaria ||
		'';

	if (faixaHorariaTexto) return faixaHorariaTexto;

	const horaInicio = horario?.hora_inicio || item?.hora_inicio;
	const horaFim = horario?.hora_fim || item?.hora_fim;

	if (horaInicio || horaFim) {
		return `${formatarHora(horaInicio)} às ${formatarHora(horaFim)}`;
	}

	if (tipoItem === 'alocacao') {
		return `${formatarHora(item.hora_inicio)} às ${formatarHora(item.hora_fim)}`;
	}

	return tipoLabel;
};

const construirMapaCelulas = ({ linhas, colunasSemana, itensPorId }) => {
	const mapa = {};

	colunasSemana.forEach((coluna) => {
		linhas.forEach((linha) => {
			const dataColuna = coluna?.data || '';
			const key = `${dataColuna || coluna.nome_dia_semana}-${linha.linhaKey}`;

			if (!dataColuna) {
				mapa[key] = [];
				return;
			}

			const celula = (linha.celulas || []).find(
				(itemCelula) => itemCelula?.data === dataColuna,
			);

			if (!celula) {
				mapa[key] = [];
				return;
			}

			const itensDaCelula = obterItensDaCelula(celula, itensPorId);
			mapa[key] = itensDaCelula.map((item) => ({
				item,
				celula,
				datasRepresentadas: [
					extrairDataReal(item, celula) || dataColuna,
				],
			}));
		});
	});

	return mapa;
};

const filtrarLinhasComCardsNaSemana = (linhas = [], colunasSemana = []) => {
	const datasSemana = new Set(
		(colunasSemana || [])
			.map((coluna) => String(coluna?.data || ''))
			.filter(Boolean),
	);

	if (datasSemana.size === 0) return [];

	return (linhas || [])
		.map((linha) => {
			const celulasSemana = (linha?.celulas || [])
				.map((celula) => ({
					...celula,
					cards: Array.isArray(celula?.cards) ? celula.cards : [],
					itens_ids: Array.isArray(celula?.itens_ids)
						? celula.itens_ids
						: [],
				}))
				.filter((celula) => {
					const dataCelula = String(celula?.data || '');
					if (!datasSemana.has(dataCelula)) return false;
					return (
						celula.cards.length > 0 || celula.itens_ids.length > 0
					);
				});

			if (celulasSemana.length === 0) return null;

			return {
				...linha,
				celulas: celulasSemana,
			};
		})
		.filter(Boolean);
};

const getQuantidadeCardsCelula = (celula = {}) => {
	if (Array.isArray(celula?.cards) && celula.cards.length > 0) {
		return celula.cards.length;
	}
	if (Array.isArray(celula?.itens_ids)) return celula.itens_ids.length;
	return 0;
};

const getMaiorQuantidadeCardsLinha = (linha = {}) =>
	Math.max(0, ...(linha?.celulas || []).map(getQuantidadeCardsCelula));

const criarFragmentoLinhaPdf = ({
	linha,
	inicio,
	fim,
	fragmentoIndex,
	totalCardsLinha,
}) => {
	const linhaInteira = inicio === 0 && fim >= totalCardsLinha;

	if (linhaInteira) return linha;

	return {
		...linha,
		linhaKey: `${linha.linhaKey}-parte-${fragmentoIndex + 1}`,
		celulas: (linha.celulas || []).map((celula) => ({
			...celula,
			cards: Array.isArray(celula?.cards)
				? celula.cards.slice(inicio, fim)
				: celula?.cards,
			itens_ids: Array.isArray(celula?.itens_ids)
				? celula.itens_ids.slice(inicio, fim)
				: celula?.itens_ids,
		})),
		__linhaFragmentada: true,
		__fragmentoIndex: fragmentoIndex,
	};
};

const paginarLinhasPdfPorCards = (linhas = []) => {
	const paginas = [];
	let paginaAtual = [];
	let slotsUsados = 0;
	let fragmentoIndex = 0;

	const fecharPaginaAtual = () => {
		if (paginaAtual.length === 0) return;
		paginas.push(paginaAtual);
		paginaAtual = [];
		slotsUsados = 0;
	};

	(linhas || []).forEach((linha) => {
		const maiorQuantidadeCards = Math.max(
			0,
			...(linha?.celulas || []).map(getQuantidadeCardsCelula),
		);

		if (maiorQuantidadeCards === 0) {
			if (slotsUsados >= PDF_MAX_CARD_SLOTS_PER_PAGE) {
				fecharPaginaAtual();
			}

			paginaAtual.push(linha);
			slotsUsados += 1;
			return;
		}

		let inicio = 0;

		while (inicio < maiorQuantidadeCards) {
			if (slotsUsados >= PDF_MAX_CARD_SLOTS_PER_PAGE) {
				fecharPaginaAtual();
			}

			const slotsDisponiveis = PDF_MAX_CARD_SLOTS_PER_PAGE - slotsUsados;
			const quantidadeNesteFragmento = Math.min(
				maiorQuantidadeCards - inicio,
				slotsDisponiveis,
			);
			const fim = inicio + quantidadeNesteFragmento;
			const fragmento = criarFragmentoLinhaPdf({
				linha,
				inicio,
				fim,
				fragmentoIndex,
				totalCardsLinha: maiorQuantidadeCards,
			});

			paginaAtual.push(fragmento);
			slotsUsados += quantidadeNesteFragmento;
			inicio = fim;
			fragmentoIndex += 1;
		}
	});

	fecharPaginaAtual();

	return paginas;
};

const prepararLinhasParaPaginacaoPdf = (linhas = []) => {
	const linhasComCards = (linhas || []).filter(
		(linha) => getMaiorQuantidadeCardsLinha(linha) > 0,
	);

	if (linhasComCards.length === 0) {
		return linhas || [];
	}

	return linhas;
};

const VerticalSideLabel = ({ children, className = '' }) => {
	return (
		<div
			className={`flex h-full w-full items-center justify-center overflow-visible ${className}`}
			style={{ paddingRight: '12px' }}>
			<span
				className='inline-flex items-center justify-center whitespace-nowrap text-center leading-normal'
				style={{
					transform: 'rotate(-90deg)',
					transformOrigin: 'center center',
				}}>
				{children}
			</span>
		</div>
	);
};

const EscalaCardExport = ({ item, linha, getTipoKeyByItem }) => {
	const estiloItem = getStyleByItem(item, getTipoKeyByItem);
	const tipoLabel = getTipoLabelByItem(item, getTipoKeyByItem);
	const tipoItem = getTipoKeyByItem(item);
	// A troca usa a mesma estrutura de dados de uma alocação na grade.
	// Mantemos o destaque de troca no título, mas exibimos seu horário como
	// horário de jornada, para o cartão do PDF conter as mesmas informações.
	const mostrarHorario = tipoItem === 'alocacao' || tipoItem === 'troca';
	const mostrarObservacao = deveMostrarObservacao(item, getTipoKeyByItem);
	const mostrarEspecialidadeEVinculo = deveMostrarEspecialidadeEVinculo(
		item,
		getTipoKeyByItem,
	);
	const conteudoSecundario = getConteudoSecundarioItem(
		item,
		tipoItem,
		tipoLabel,
	);
	const tituloCard = (
		item?.resumo?.titulo ||
		item?.nome_profissional ||
		'VACÂNCIA'
	).toUpperCase();
	const tituloCardResumido = smartTruncateName(tituloCard);
	const linhaOrigem = item?.__linhaOrigem || linha;
	const vinculoItemLabel = getVinculoItem(item, linhaOrigem) || 'Sem vínculo';
	const especialidadeItemLabel =
		getEspecialidadeItem(item, linhaOrigem) || 'Área de atuação';
	const visualVinculo = getVisualVinculoPorNome(vinculoItemLabel);
	const corEspecialidade = linha?.especialidadeTextoCor || '#4F86A8';
	const isVacancia = tipoItem === 'vacancia';

	return (
		<div className='relative flex h-[165px] w-[99%] mx-auto flex-col gap-[6px] overflow-hidden rounded-[9px] border border-[#dfdfdf] bg-white py-[10px] pl-[17px] pr-[12px] shadow'>
			{mostrarEspecialidadeEVinculo && (
				<span
					className='absolute left-0 top-0 h-full w-[6px]'
					style={{ backgroundColor: corEspecialidade }}
				/>
			)}
			<div
				title={tituloCard}
				className={`flex min-h-[30px] w-full items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[4px] px-2 text-center text-[13px] font-bold leading-[16px] ${
					isVacancia ? 'py-2' : 'h-[30px] max-h-[30px]'
				}`}
				style={estiloItem.badgeStyle}>
				<span className='relative -top-px block pb-3'>
					{tituloCardResumido}
				</span>
			</div>
			<div
				className={`text-center text-[12px] font-inter leading-[15px] ${
					conteudoSecundario === '' ? 'hidden' : ''
				} ${mostrarHorario ? 'font-normal' : 'font-semibold'}`}
				style={
					mostrarHorario
						? { color: '#B8B8B8' }
						: estiloItem.typeTextStyle
				}>
				<span className='relative -top-px inline-block pb-3'>
					{conteudoSecundario}
				</span>
			</div>
			{mostrarEspecialidadeEVinculo && (
				<>
					<div
						className={`flex h-[25px] min-h-[25px] max-h-[25px] w-full min-w-0 items-center justify-center overflow-hidden rounded-full border border-[#ececec] bg-[#f8f8f8] px-2 text-center text-[12px] text-[#65686d] whitespace-nowrap ${
							isVacancia ? 'mt-auto' : ''
						}`}>
						<span className='relative -top-px block min-w-0 max-w-full truncate overflow-hidden whitespace-nowrap leading-[15px] pb-3'>
							{especialidadeItemLabel}
						</span>
					</div>
					<div
						className='flex h-[25px] min-h-[25px] max-h-[25px] w-full min-w-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border px-1 text-center text-[12px] font-semibold uppercase whitespace-nowrap'
						style={{
							borderColor: visualVinculo.borda,
							backgroundColor: visualVinculo.fundo,
							color: visualVinculo.texto,
						}}>
						<span
							className='h-1.5 w-1.5 shrink-0 rounded-full'
							style={{ backgroundColor: visualVinculo.texto }}
						/>
						<span className='relative -top-px block min-w-0 max-w-full truncate overflow-hidden whitespace-nowrap leading-[15px] pb-3'>
							{vinculoItemLabel}
						</span>
					</div>
				</>
			)}
			{mostrarObservacao && (
				<div className='flex flex-1 items-center justify-center overflow-hidden rounded-[8px] border border-[#ececec] px-2 py-[4px] text-center text-[13px] leading-[16px] text-[#65686d]'>
					{item?.resumo?.observacao &&
					item?.resumo?.observacao.trim() !== ''
						? item?.resumo?.observacao
						: 'Sem observação'}
				</div>
			)}
		</div>
	);
};

const EscalaSetorExport = ({
	data,
	setor,
	visualizacaoSetores,
	colunasSemana,
	itensPorId,
	getTipoKeyByItem,
	semanaSelecionadaIndex,
	quantidadeSemanas,
	semRegistrosNoMes = false,
	linhasOverride = null,
	legendaAgrupamentos = [],
	agruparPorEspecialidade = false,
	regime = '',
	isLastPage,
}) => {
	const contentRef = useRef(null);
	const [layoutState, setLayoutState] = useState({
		scale: 1,
		width: PDF_PAGE_WIDTH - PDF_PAGE_PADDING_X * 2,
		height: PDF_PAGE_HEIGHT - PDF_PAGE_PADDING_Y * 2,
	});

	const linhas = useMemo(
		() =>
			Array.isArray(linhasOverride)
				? linhasOverride
				: construirLinhasSetorAtualizada({
						setor,
						itensPorId,
						data,
						agruparPorEspecialidade,
						regime,
					}).linhas,
		[
			setor,
			itensPorId,
			data,
			linhasOverride,
			agruparPorEspecialidade,
			regime,
		],
	);
	const celulasPorDiaELinha = useMemo(
		() =>
			construirMapaCelulas({
				linhas,
				colunasSemana,
				itensPorId,
			}),
		[linhas, colunasSemana, itensPorId],
	);
	const turnoSpanPorLinha = useMemo(() => {
		const spans = construirTurnoSpan(linhas);
		if (!agruparPorEspecialidade) return spans;

		let index = 0;
		while (index < linhas.length) {
			const turnoAtual = linhas[index].turnoKey;
			let fim = index;
			while (fim < linhas.length && linhas[fim].turnoKey === turnoAtual) {
				fim += 1;
			}
			const titulos = linhas
				.slice(index, fim)
				.filter((linha) => linha.__temAgrupamentoReal).length;
			spans[linhas[index].linhaKey].rowSpan += titulos;
			index = fim;
		}
		return spans;
	}, [linhas, agruparPorEspecialidade]);
	const deveExibirTitulosAgrupamento =
		agruparPorEspecialidade &&
		linhas.some((linha) => linha.__temAgrupamentoReal);
	const totalLinhasCorpoTabela =
		linhas.length +
		(deveExibirTitulosAgrupamento
			? linhas.filter((linha) => linha.__temAgrupamentoReal).length
			: 0);

	const totalLeitosEscala = (() => {
		const q = Number(setor?.quantidade_leitos);
		if (Number.isFinite(q) && q > 0) return q;
		const mapaSalas = new Map();
		(setor?.linhas || []).forEach((linha) => {
			const salaId = linha?.sala?.id;
			const qtd = Number(linha?.sala?.quantidade_leitos ?? 0);
			if (!salaId || !Number.isFinite(qtd)) return;
			if (mapaSalas.has(salaId)) return;
			mapaSalas.set(salaId, qtd);
		});
		return [...mapaSalas.values()].reduce((t, n) => t + n, 0);
	})();

	const capacidadeLabel = (() => {
		const quantidadeCapacidade = Number(
			setor?.quantidade_capacidade ?? setor?.quantidade_leitos,
		);
		const tipoRaw = String(setor?.tipo_capacidade || 'LEITO')
			.trim()
			.toUpperCase();
		const tipoPlural = tipoRaw.endsWith('S') ? tipoRaw : `${tipoRaw}S`;
		const quantidadeVal =
			Number.isFinite(quantidadeCapacidade) && quantidadeCapacidade > 0
				? quantidadeCapacidade
				: totalLeitosEscala || 0;
		const tipoFinal =
			Number.isFinite(quantidadeCapacidade) && quantidadeCapacidade > 0
				? tipoPlural
				: 'LEITOS';
		return `${quantidadeVal} ${tipoFinal}`;
	})();

	const semanaLabel = (() => {
		const datas = colunasSemana
			.map((coluna) => coluna?.data)
			.filter(Boolean)
			.sort();

		if (datas.length === 0) {
			return `Semana ${semanaSelecionadaIndex + 1} de ${quantidadeSemanas}`;
		}

		return `${formatarDataPtBr(datas[0])} a ${formatarDataPtBr(
			datas[datas.length - 1],
		)}`;
	})();

	useLayoutEffect(() => {
		const node = contentRef.current;
		if (!node) return;

		const nextWidth = node.scrollWidth || 1;
		const nextHeight = node.scrollHeight || 1;
		const maxWidth = PDF_PAGE_WIDTH - PDF_PAGE_PADDING_X * 2;
		const nextScale = Math.min(1, maxWidth / nextWidth);

		setLayoutState({
			scale: nextScale > 0 ? nextScale : 1,
			width: nextWidth,
			height: nextHeight,
		});
	}, [linhas, colunasSemana, celulasPorDiaELinha]);

	return (
		<section
			data-escala-pdf-page='true'
			className='bg-white overflow-hidden'
			style={{
				width: `${PDF_PAGE_WIDTH}px`,
				height: `${PDF_PAGE_HEIGHT}px`,
				pageBreakAfter: isLastPage ? 'auto' : 'always',
				breakAfter: isLastPage ? 'auto' : 'page',
			}}>
			<div
				className='h-full w-full overflow-hidden bg-white'
				style={{
					padding: `${PDF_PAGE_PADDING_Y}px ${PDF_PAGE_PADDING_X}px`,
				}}>
				<div
					style={{
						width: `${layoutState.width * layoutState.scale}px`,
						height: `${layoutState.height * layoutState.scale}px`,
					}}>
					<div
						ref={contentRef}
						className='mt-0 items-center px-8 py-5'
						style={{
							width: `${layoutState.width}px`,
							transform: `scale(${layoutState.scale})`,
							transformOrigin: 'top left',
						}}>
						<div className='mb-3 flex flex-col gap-3'>
							<div className='flex flex-col sm:flex-row justify-between sm:items-end gap-2'>
								<p className='text-[#757575] text-sm'>
									Última atualização:{' '}
									{data?.resumo?.ultima_atualizacao ||
									data.ultima_atualizacao
										? new Date(
												data?.resumo
													?.ultima_atualizacao ||
													data.ultima_atualizacao,
											).toLocaleDateString('pt-BR', {
												day: '2-digit',
												month: '2-digit',
												year: 'numeric',
											})
										: '--'}
								</p>

								<p className='text-[#757575] text-sm text-right'>
									Unidade: {getNomeUnidade(data, setor)} |
									Setor: {linhas[0]?.setorNome || '--'}
								</p>
							</div>
						</div>

						{semRegistrosNoMes ? (
							<div className='mt-6 rounded-xl border border-[#d9dee5] bg-[#f8fafc] px-8 py-10 text-center'>
								<p className='text-[20px] font-semibold text-[#4F5257] leading-normal'>
									Este setor não possui nenhum registro de
									escala no mês.
								</p>
								<p className='mt-2 text-[14px] text-[#7a8088] leading-normal'>
									Nenhum profissional ou vacância foi
									encontrado para este setor.
								</p>
							</div>
						) : (
							<div className='rounded-md overflow-visible'>
								<div
									className='border border-[#D8DEE8] border-b-0 bg-[#FCFCFC] px-3 py-[7px] rounded-t-lg leading-3'
									style={{ width: '102%' }}>
									<div className='flex min-h-[28px] items-center justify-between gap-4'>
										<div className='shrink-0 text-[#4F5257] font-medium text-[15px]'>
											{capacidadeLabel}
										</div>
										{legendaAgrupamentos.length > 0 && (
											<div className='flex flex-wrap items-center justify-end gap-x-3 gap-y-1'>
												{legendaAgrupamentos.map(
													(agrupamento) => (
														<div
															key={
																agrupamento.key
															}
															className='flex items-center gap-1.5 text-[11px] font-medium uppercase'
															style={{
																color: agrupamento.texto,
															}}>
															<span
																className='h-2.5 w-2.5 rounded-full border'
																style={{
																	backgroundColor:
																		agrupamento.fundo,
																	borderColor:
																		agrupamento.texto,
																}}
															/>
									<span className='relative -top-px pb-3 leading-[13px]'>
																{
																	agrupamento.nome
																}
															</span>
														</div>
													),
												)}
											</div>
										)}
									</div>
								</div>
								<table className='w-full table-fixed'>
									<thead>
										<tr>
											<th className='w-[40px] bg-[#0B4D7A] border-r border-[#d5d5d5] '></th>
											{colunasSemana.map((colunaDia) => (
												<th
													key={`${colunaDia.nome_dia_semana}-${colunaDia.data || 'sem-data'}`}
													className='w-[206px] bg-[#0B4D7A] text-white text-[18px] font-semibold text-center pb-[14px] leading-[18px] border-r border-[#d5d5d5] last:border-r-0'>
													<div className='leading-normal flex min-h-[44px] flex-col items-center justify-center'>
														<div className='relative -top-px leading-[20px]'>
															{
																colunaDia.nome_dia_semana
															}
														</div>
														<div className='relative -top-px text-[12px] font-normal leading-[15px]'>
															{colunaDia.data
																? formatarDataPtBr(
																		colunaDia.data,
																	)
																: '--'}
														</div>
													</div>
												</th>
											))}
											<th className='w-[44px] min-w-[44px] bg-[#0B4D7A] border-l border-[#d5d5d5] '></th>
										</tr>
									</thead>
									<tbody className='rounded-b-md border border-t border-l-blue-[#0B4D7A] overflow-hidden'>
										{linhas.map((linha, rowIndex) => {
											const mostrarTituloAgrupamento =
												deveExibirTitulosAgrupamento &&
												linha.__temAgrupamentoReal;
											const iniciaNovoTurno =
												rowIndex > 0 &&
												linhas[rowIndex - 1]
													?.turnoKey !==
													linha.turnoKey;
											const turnoSpan =
												turnoSpanPorLinha[
													linha.linhaKey
												];

											return (
												<React.Fragment
													key={linha.linhaKey}>
													{mostrarTituloAgrupamento && (
														<tr>
															{rowIndex === 0 && (
																<td
																	rowSpan={
																		totalLinhasCorpoTabela
																	}
																	className='bg-[#0B4D7A] border-r border-[#d5d5d5] align-middle'>
																	<VerticalSideLabel className='text-white text-[18px] font-semibold tracking-wide'>
																		{linhas[0]
																			?.setorNome
																			? linhas[0].setorNome.toUpperCase()
																			: 'SETOR'}
																	</VerticalSideLabel>
																</td>
															)}
															<td
																colSpan={
																	colunasSemana.length
																}
																className='px-1.5 py-1'
																style={getLinhaTurnoStyle(
																	linha,
																)}>
																<div
																	className='flex w-full items-center rounded-[7px] px-3 py-1.5 text-[13px] font-bold uppercase leading-[16px] tracking-[0.08em] text-white shadow-sm'
																	style={{
																		backgroundColor:
																			linha.especialidadeTextoCor ||
																			'#0B4D7A',
																	}}>
											<span className='relative -top-px block pb-3'>
																		{
																			linha.agrupamentoNome
																		}
																	</span>
																</div>
															</td>
															{turnoSpan?.show && (
																<td
																	rowSpan={
																		turnoSpan.rowSpan
																	}
																	className='w-[44px] min-w-[44px] max-w-[44px] overflow-hidden bg-[#0B4D7A] border border-[#E3E3E3] align-middle'>
																	<VerticalSideLabel className='text-[14px] sm:text-[18px] text-white font-semibold whitespace-nowrap'>
																		{linha.turnoNome.toUpperCase()}
																	</VerticalSideLabel>
																</td>
															)}
														</tr>
													)}
													<tr>
														{rowIndex === 0 &&
															!mostrarTituloAgrupamento && (
																<td
																	rowSpan={
																		totalLinhasCorpoTabela
																	}
																	className='bg-[#0B4D7A] border-r border-[#d5d5d5] align-middle'>
																	<VerticalSideLabel className='text-white text-[18px] font-semibold tracking-wide'>
																		{linhas[0]
																			?.setorNome
																			? linhas[0].setorNome.toUpperCase()
																			: 'SETOR'}
																	</VerticalSideLabel>
																</td>
															)}
														{colunasSemana.map(
															(colunaDia) => {
																const key = `${colunaDia.data || colunaDia.nome_dia_semana}-${linha.linhaKey}`;
																const itensSemanais =
																	celulasPorDiaELinha[
																		key
																	] || [];
																return (
																	<td
																		key={
																			key
																		}
																		className={`w-[206px] align-top border-r border-b border-[rgba(11,77,122,0.08)] p-1.5 ${
																			iniciaNovoTurno
																				? 'border-t-2 border-t-[#C7D2DF]'
																				: ''
																		}`}
																		style={getLinhaTurnoStyle(
																			linha,
																		)}>
																		<div className='flex min-h-[165px] flex-col gap-2'>
																			{itensSemanais.map(
																				(
																					{
																						item,
																					},
																					itemIndex,
																				) => (
																					<EscalaCardExport
																						key={`${item?.id || 'item'}-${itemIndex}`}
																						item={
																							item
																						}
																						linha={
																							linha
																						}
																						getTipoKeyByItem={
																							getTipoKeyByItem
																						}
																					/>
																				),
																			)}
																		</div>
																	</td>
																);
															},
														)}
														{turnoSpan?.show &&
															!mostrarTituloAgrupamento && (
																<td
																	rowSpan={
																		turnoSpan.rowSpan
																	}
																	className={`w-[44px] min-w-[44px] max-w-[44px] overflow-hidden bg-[#0B4D7A] border border-[#E3E3E3] align-middle ${
																		iniciaNovoTurno
																			? 'border-t-2 border-t-[#C7D2DF]'
																			: ''
																	}`}>
																	<VerticalSideLabel className='text-[14px] sm:text-[18px] text-white font-semibold whitespace-nowrap'>
																		{linha.turnoNome.toUpperCase()}
																	</VerticalSideLabel>
																</td>
															)}
													</tr>
												</React.Fragment>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						<div className='mt-3 border-t border-[#D8DEE8] pt-2 text-right text-[11px] font-semibold text-[#6a7786]'>
							Semana: {semanaLabel}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

const EscalaPdfExportRoot = ({
	visualizacaoSetores,
	data,
	itensPorId,
	semanaSelecionadaIndex,
	getTipoKeyByItem,
	agruparPorEspecialidade = false,
	regime = '',
}) => {
	const semanasDoMes = buildSemanasDoMes(data?.visualizacao?.colunas || []);
	const totalSemanas = Math.max(semanasDoMes.length, 1);
	const paginas = useMemo(() => {
		const semanasParaExportar =
			semanasDoMes.length > 0
				? semanasDoMes.map((_, index) => index)
				: [
						typeof semanaSelecionadaIndex === 'number'
							? semanaSelecionadaIndex
							: 0,
					];

		return visualizacaoSetores.flatMap((setor) => {
			if (!setorTemRegistrosNoMes(setor, itensPorId)) {
				const indiceSemana =
					typeof semanaSelecionadaIndex === 'number'
						? semanaSelecionadaIndex
						: 0;

				return [
					{
						setor,
						indiceSemana,
						semRegistrosNoMes: true,
						colunasSemana: construirColunasSemana(
							data?.visualizacao || {},
							indiceSemana,
						),
						linhasPagina: null,
						legendaAgrupamentos: [],
					},
				];
			}

			const estruturaLinhas = agruparPorEspecialidade
				? construirLinhasSetorAtualizada({
						setor,
						itensPorId,
						data,
						agruparPorEspecialidade,
						regime,
					})
				: {
						linhas: construirLinhasSetor(setor, itensPorId, data)
							.linhas,
						legendaAgrupamentos: [],
					};
			const { linhas, legendaAgrupamentos } = estruturaLinhas;

			return semanasParaExportar.flatMap((indiceSemana) => {
				const colunasSemana = construirColunasSemana(
					data?.visualizacao || {},
					indiceSemana,
				);
				const linhasDaSemana = filtrarLinhasComCardsNaSemana(
					linhas,
					colunasSemana,
				);
				const legendaSemana = legendaAgrupamentos.filter(
					(agrupamento) =>
						linhasDaSemana.some(
							(linha) =>
								normalizarTexto(
									linha?.agrupamentoNome || '',
								) === agrupamento.key,
						),
				);

				if (linhasDaSemana.length === 0) return [];

				const linhasPaginaveis =
					prepararLinhasParaPaginacaoPdf(linhasDaSemana);
				const chunks = paginarLinhasPdfPorCards(linhasPaginaveis);

				return (chunks.length ? chunks : [linhasPaginaveis]).map(
					(linhasPagina, chunkIndex) => ({
						setor,
						indiceSemana,
						semRegistrosNoMes: false,
						colunasSemana,
						linhasPagina,
						legendaAgrupamentos: legendaSemana,
						chunkIndex,
					}),
				);
			});
		});
	}, [
		data,
		itensPorId,
		semanaSelecionadaIndex,
		semanasDoMes,
		visualizacaoSetores,
		agruparPorEspecialidade,
		regime,
	]);

	return (
		<div className='bg-white'>
			{paginas.map(
				(
					{
						setor,
						indiceSemana,
						colunasSemana,
						semRegistrosNoMes,
						linhasPagina,
						legendaAgrupamentos,
						chunkIndex = 0,
					},
					index,
				) => (
					<EscalaSetorExport
						key={`${setor.id || `setor-${index}`}-semana-${indiceSemana}-parte-${chunkIndex}`}
						data={data}
						setor={setor}
						visualizacaoSetores={visualizacaoSetores}
						colunasSemana={colunasSemana}
						itensPorId={itensPorId}
						getTipoKeyByItem={getTipoKeyByItem}
						semanaSelecionadaIndex={indiceSemana || 0}
						quantidadeSemanas={totalSemanas}
						semRegistrosNoMes={semRegistrosNoMes}
						linhasOverride={linhasPagina}
						legendaAgrupamentos={legendaAgrupamentos}
						agruparPorEspecialidade={agruparPorEspecialidade}
						regime={regime}
						isLastPage={index === paginas.length - 1}
					/>
				),
			)}
		</div>
	);
};

export const EscalaPdfPreview = ({
	visualizacaoSetores,
	data,
	itensPorId,
	semanaSelecionadaIndex,
	getTipoKeyByItem,
	agruparPorEspecialidade = false,
	regime = '',
	className = '',
	onExport,
	isExporting = false,
}) => {
	if (
		!Array.isArray(visualizacaoSetores) ||
		visualizacaoSetores.length === 0
	) {
		return null;
	}

	const totalSemanas = Math.max(
		buildSemanasDoMes(data?.visualizacao?.colunas || []).length,
		1,
	);
	const totalPaginas = visualizacaoSetores.length * totalSemanas;

	return (
		<div
			className={`rounded-xl border border-[#d9dee5] bg-[#eef2f6] p-4 ${className}`}>
			<div className='mb-3 flex items-center justify-between gap-3'>
				<div>
					<p className='text-[16px] font-semibold text-[#23415f]'>
						Pré-visualização do PDF
					</p>
					<p className='text-[12px] text-[#66788a]'>
						Renderização em tela do layout que será exportado.
					</p>
				</div>
				<div className='flex items-center gap-2'>
					<span className='rounded-full bg-white px-3 py-1 text-[11px] font-medium text-[#6a7786] border border-[#d7dde5]'>
						{totalPaginas} página(s)
					</span>
					{onExport && (
						<button
							type='button'
							onClick={onExport}
							disabled={isExporting}
							className='rounded-md bg-[#B74644] px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#9e3836] disabled:cursor-not-allowed disabled:opacity-60'>
							{isExporting ? 'Gerando PDF...' : 'Exportar PDF'}
						</button>
					)}
				</div>
			</div>

			<div className='max-h-[85vh] overflow-auto rounded-lg border border-[#d7dde5] bg-[#dfe6ee] p-4'>
				<div className='flex flex-col gap-6 min-w-max'>
					<EscalaPdfExportRoot
						visualizacaoSetores={visualizacaoSetores}
						data={data}
						itensPorId={itensPorId}
						semanaSelecionadaIndex={semanaSelecionadaIndex}
						getTipoKeyByItem={getTipoKeyByItem}
						agruparPorEspecialidade={agruparPorEspecialidade}
						regime={regime}
					/>
				</div>
			</div>
		</div>
	);
};

const aguardarRender = async () => {
	await new Promise((resolve) => requestAnimationFrame(resolve));
	if (document.fonts?.status !== 'loaded' && document.fonts?.ready) {
		await document.fonts.ready;
	}
	await new Promise((resolve) => setTimeout(resolve, 20));
};

const getFileName = (data) => {
	const serviceName = String(data?.escala?.nome_servico || 'servico')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-');

	return `escalas-${serviceName || 'servico'}.pdf`;
};

const capturarPaginaPdf = async (pagina) => {
	const canvas = await html2canvas(pagina, {
		backgroundColor: '#ffffff',
		useCORS: true,
		scale: PDF_CAPTURE_SCALE,
		logging: false,
		windowWidth: PDF_PAGE_WIDTH,
		windowHeight: PDF_PAGE_HEIGHT,
		imageTimeout: 0,
		removeContainer: true,
	});

	return canvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
};

const capturarPaginasPdf = async (paginas) => {
	const imageDataByIndex = new Array(paginas.length);
	let nextIndex = 0;

	const workers = Array.from(
		{ length: Math.min(PDF_CAPTURE_CONCURRENCY, paginas.length) },
		async () => {
			while (nextIndex < paginas.length) {
				const index = nextIndex;
				nextIndex += 1;
				imageDataByIndex[index] = await capturarPaginaPdf(
					paginas[index],
				);
			}
		},
	);

	await Promise.all(workers);
	return imageDataByIndex;
};

const gerarPdfDoDomRenderizado = async (mountNode, filename) => {
	const paginas = Array.from(
		mountNode.querySelectorAll('[data-escala-pdf-page="true"]'),
	);

	if (!paginas.length) return;

	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'px',
		format: [PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT],
		compress: true,
	});

	const imagensPaginas = await capturarPaginasPdf(paginas);
	for (let index = 0; index < imagensPaginas.length; index += 1) {
		if (index > 0) {
			doc.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT], 'portrait');
		}

		doc.addImage(
			imagensPaginas[index],
			'JPEG',
			0,
			0,
			PDF_PAGE_WIDTH,
			PDF_PAGE_HEIGHT,
			undefined,
			'FAST',
		);
	}

	doc.save(filename);
};

export const exportarPdfGridEscala = async ({
	visualizacaoSetores,
	data,
	itensPorId,
	getTipoKeyByItem,
	semanaSelecionadaIndex,
	agruparPorEspecialidade = false,
	regime = '',
}) => {
	if (
		!Array.isArray(visualizacaoSetores) ||
		visualizacaoSetores.length === 0 ||
		typeof document === 'undefined'
	) {
		return;
	}

	const mountNode = document.createElement('div');
	mountNode.setAttribute('data-escala-pdf-export', 'true');
	mountNode.style.position = 'fixed';
	mountNode.style.left = '-20000px';
	mountNode.style.top = '0';
	mountNode.style.width = `${PDF_PAGE_WIDTH}px`;
	mountNode.style.background = '#ffffff';
	mountNode.style.zIndex = '-1';
	mountNode.style.overflow = 'hidden';
	document.body.appendChild(mountNode);

	const root = createRoot(mountNode);

	try {
		root.render(
			<EscalaPdfExportRoot
				visualizacaoSetores={visualizacaoSetores}
				data={data}
				itensPorId={itensPorId}
				semanaSelecionadaIndex={semanaSelecionadaIndex}
				getTipoKeyByItem={getTipoKeyByItem}
				agruparPorEspecialidade={agruparPorEspecialidade}
				regime={regime}
			/>,
		);

		await aguardarRender();

		await gerarPdfDoDomRenderizado(mountNode, getFileName(data));
	} finally {
		root.unmount();
		if (mountNode.parentNode) {
			mountNode.parentNode.removeChild(mountNode);
		}
	}
};
